/**
 * The ONE place per-platform publish truth is computed.
 *
 * Rule: a platform is `published` IFF there is an evidence entry for THAT
 * platform proving it. Never inferred from `social_posts.status`, and never
 * from `social_posts.platforms` (which is the user's INTENT, not a delivery
 * record — a "Post now" to one platform leaves the other entries untouched).
 *
 * Deriving per-platform state from the row status is what let a single-platform
 * Instagram publish render "Facebook · published" and "LinkedIn · published"
 * for platforms that were never contacted.
 */

export type PlatformOutcome =
  /** No evidence at all — this platform was never dispatched. */
  | 'not_attempted'
  /** Scheduled or mid-flight. */
  | 'pending'
  /** Positive proof: success + a real platform post id. */
  | 'published'
  /** The platform may have accepted it, but we hold no usable id. Never a success, never a clean failure. */
  | 'unverified'
  /** The platform rejected it. */
  | 'failed';

export interface PlatformOutcomeDetail {
  outcome: PlatformOutcome;
  postId: string | null;
  permalink: string | null;
  error: string | null;
}

interface EvidenceEntry {
  platform?: unknown;
  status?: unknown;
  platformPostId?: unknown;
  permalink?: unknown;
  error?: unknown;
  outcome?: unknown;
}

function asArray(value: unknown): EvidenceEntry[] {
  return Array.isArray(value) ? (value as EvidenceEntry[]) : [];
}

function str(value: unknown): string | null {
  return typeof value === 'string' && value.trim().length > 0 ? value : null;
}

/**
 * @param isValidId injected so this module stays free of platform-shape rules
 *   (the validator lives in platform-post-id.ts and is shared with the
 *   dispatcher, so success here means exactly what success meant at dispatch).
 */
export function platformOutcome(
  platform: string,
  platformResponse: unknown,
  platformSchedules: unknown,
  isValidId: (platform: string, id: unknown) => boolean,
  /**
   * Optional read-back result (social_posts.platform_verification). ONLY a
   * positive `missing` — the platform saying the post is not there — may
   * downgrade a published platform. `indeterminate` deliberately changes
   * nothing: an inconclusive check is not evidence of failure.
   */
  platformVerification?: unknown,
): PlatformOutcomeDetail {
  const verification =
    platformVerification &&
    typeof platformVerification === 'object' &&
    !Array.isArray(platformVerification)
      ? (platformVerification as Record<string, { state?: unknown; detail?: unknown }>)[platform]
      : undefined;
  const verifiedMissing = verification?.state === 'missing';
  // 1. An explicit per-platform schedule entry is authoritative for the
  //    staggered path — that writer already records per-platform truth.
  const schedules =
    platformSchedules && typeof platformSchedules === 'object' && !Array.isArray(platformSchedules)
      ? (platformSchedules as Record<string, { status?: unknown }>)
      : null;
  const scheduled = schedules?.[platform];
  const scheduledStatus = scheduled ? str(scheduled.status) : null;

  // 2. Dispatch evidence.
  const entry = asArray(platformResponse).find((e) => e.platform === platform);

  if (entry) {
    const postId = str(entry.platformPostId);
    const permalink = str(entry.permalink);
    const error = str(entry.error);

    if (entry.status === 'success') {
      // A recorded success WITHOUT a usable id cannot be shown as published:
      // there is nothing to link to, verify, or measure.
      if (!isValidId(platform, postId)) {
        return { outcome: 'unverified', postId: null, permalink, error };
      }
      // We were told it published AND we have an id — but a read-back has since
      // proved the post is not there. Publish-time evidence loses to a positive
      // absence: keeping it 'published' is exactly the stale claim this whole
      // mechanism exists to catch.
      if (verifiedMissing) {
        return {
          outcome: 'failed',
          postId,
          permalink,
          error:
            typeof verification?.detail === 'string'
              ? verification.detail
              : 'The platform reports this post does not exist.',
        };
      }
      return { outcome: 'published', postId, permalink, error: null };
    }
    // outcome:'unknown' means the platform may have accepted it — reporting a
    // hard failure there would be its own false claim.
    if (entry.outcome === 'unknown') {
      return { outcome: 'unverified', postId: null, permalink, error };
    }
    return { outcome: 'failed', postId: null, permalink, error };
  }

  if (scheduledStatus) {
    if (scheduledStatus === 'published') {
      // No dispatch evidence to corroborate it — treat as unverified, not published.
      return { outcome: 'unverified', postId: null, permalink: null, error: null };
    }
    if (scheduledStatus === 'failed' || scheduledStatus === 'failed_connection_removed') {
      return { outcome: 'failed', postId: null, permalink: null, error: null };
    }
    return { outcome: 'pending', postId: null, permalink: null, error: null };
  }

  // 3. No evidence of any kind. Deliberately NOT falling back to row status.
  return { outcome: 'not_attempted', postId: null, permalink: null, error: null };
}

/**
 * Roll per-platform outcomes up into a row status.
 *
 * `intended` must be the row's full platform set, not the dispatched subset —
 * computing the denominator from only the platforms in the request is what let
 * a one-platform publish mark a three-platform row 'published'.
 */
export function rollupRowStatus(outcomes: PlatformOutcome[]): string {
  if (outcomes.length === 0) return 'failed';
  const has = (o: PlatformOutcome) => outcomes.includes(o);
  const every = (o: PlatformOutcome) => outcomes.every((x) => x === o);

  if (every('published')) return 'published';
  if (every('failed')) return 'failed';
  // Anything delivered alongside anything not delivered is partial.
  if (has('published') && (has('failed') || has('unverified'))) return 'partially_published';
  // Nothing delivered, but some outcome is genuinely unknown — not a clean failure.
  if (has('unverified') && !has('published')) return 'partially_published';
  // Some platforms still owed an attempt.
  if (has('not_attempted') || has('pending')) {
    return has('published') || has('failed') ? 'partially_scheduled' : 'scheduled';
  }
  return 'failed';
}

/** Merge fresh dispatch results over prior evidence, keyed by platform. */
export function mergeEvidence(prior: unknown, fresh: unknown): EvidenceEntry[] {
  const byPlatform = new Map<string, EvidenceEntry>();
  for (const e of asArray(prior)) {
    const p = str(e.platform);
    if (p) byPlatform.set(p, e);
  }
  // Fresh results win — they describe the attempt that just happened.
  for (const e of asArray(fresh)) {
    const p = str(e.platform);
    if (p) byPlatform.set(p, e);
  }
  return [...byPlatform.values()];
}

/** First human-readable failure reason across the evidence, for error_message. */
export function firstFailureMessage(evidence: unknown): string | null {
  for (const e of asArray(evidence)) {
    if (e.status !== 'success') {
      const msg = str(e.error);
      if (msg) return msg;
    }
  }
  return null;
}
